import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { getHistoricalProductionData } from '../utils/api';
import './EndLineHistory.css';

const statusColors = {
  Passed: 'status-passed',
  Repaired: 'status-repaired',
  Damaged: 'status-damaged',
  Defected: 'status-defected',
};

function EndLineHistory() {
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');
  const [error, setError] = useState(null);
  const hasInitiallyFetched = useRef(false);

  // Fetch data from API
  const fetchHistoricalData = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getHistoricalProductionData(selectedDate);
      
      // Backend now returns a flat array with entry_type field
      const flattenedData = result.map(item => ({
        ...item,
        status: item.entry_type
      }));

      setData(flattenedData);
      hasInitiallyFetched.current = true;
    } catch (error) {
      console.error('Error fetching historical data:', error);
      setError('Failed to load historical data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch when date changes AND we've already fetched initially
    if (hasInitiallyFetched.current) {
      fetchHistoricalData();
    }
  }, [selectedDate]);

  // Fetch data on initial mount (when user clicks History)
  useEffect(() => {
    if (!hasInitiallyFetched.current) {
      fetchHistoricalData();
    }
  }, []);

  // Define columns
  const columns = useMemo(
    () => [
      {
        accessorKey: 'posting_datetime',
        header: 'Time',
        cell: ({ getValue }) => {
          const datetime = getValue();
          try {
            return format(new Date(datetime), 'HH:mm:ss');
          } catch (e) {
            return datetime;
          }
        },
        size: 100,
      },
      {
        accessorKey: 'sales_order',
        header: 'Sales Order',
        size: 130,
      },
      {
        accessorKey: 'item',
        header: 'Item',
        size: 180,
      },
      {
        accessorKey: 'colour',
        header: 'Colour',
        size: 120,
      },
      {
        accessorKey: 'fit',
        header: 'Fit',
        size: 120,
      },
      {
        accessorKey: 'size',
        header: 'Size',
        size: 80,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ getValue }) => {
          const status = getValue();
          return (
            <span className={`status-badge ${statusColors[status]}`}>
              {status}
            </span>
          );
        },
        size: 120,
      },
      {
        accessorKey: 'created_by',
        header: 'Created By',
        size: 120,
      },
    ],
    []
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    initialState: {
      pagination: {
        pageSize: 25,
      },
    },
  });

  return (
    <div className="end-line-history">
      <div className="history-container">
        {/* Filters */}
        <div className="filters-card">
          <div className="filters-grid">
            <div className="filter-group">
              <label>Date</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                max={format(new Date(), 'yyyy-MM-dd')}
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label>Search All Columns</label>
              <input
                type="text"
                value={globalFilter ?? ''}
                onChange={(e) => setGlobalFilter(e.target.value)}
                placeholder="Search everything..."
                className="filter-input"
              />
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="table-card">
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading data...</p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="desktop-table">
                <table className="data-table">
                  <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th
                            key={header.id}
                            onClick={header.column.getToggleSortingHandler()}
                            className="table-header"
                          >
                            <div className="header-content">
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              <span className="sort-indicator">
                                {{
                                  asc: ' 🔼',
                                  desc: ' 🔽',
                                }[header.column.getIsSorted()] ?? null}
                              </span>
                            </div>
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.map((row) => (
                      <tr key={row.id} className="table-row">
                        {row.getVisibleCells().map((cell) => (
                          <td key={cell.id} className="table-cell">
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile/Tablet Card View */}
              <div className="mobile-cards">
                {table.getRowModel().rows.map((row) => (
                  <div key={row.id} className="mobile-card">
                    <div className="mobile-card-header">
                      <div>
                        <p className="mobile-card-title">
                          {row.original.sales_order}
                        </p>
                        <p className="mobile-card-time">
                          {format(new Date(row.original.posting_datetime), 'HH:mm:ss')}
                        </p>
                      </div>
                      <span className={`status-badge ${statusColors[row.original.status]}`}>
                        {row.original.status}
                      </span>
                    </div>
                    <div className="mobile-card-details">
                      <div className="mobile-card-field">
                        <span className="field-label">Item:</span>
                        <p className="field-value">{row.original.item}</p>
                      </div>
                      <div className="mobile-card-field">
                        <span className="field-label">Colour:</span>
                        <p className="field-value">{row.original.colour}</p>
                      </div>
                      <div className="mobile-card-field">
                        <span className="field-label">Fit:</span>
                        <p className="field-value">{row.original.fit}</p>
                      </div>
                      <div className="mobile-card-field">
                        <span className="field-label">Size:</span>
                        <p className="field-value">{row.original.size}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              <div className="pagination">
                <div className="pagination-info">
                  <span>
                    Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
                    {Math.min(
                      (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                      table.getFilteredRowModel().rows.length
                    )}{' '}
                    of {table.getFilteredRowModel().rows.length} results
                  </span>
                </div>

                <div className="pagination-controls">
                  <select
                    value={table.getState().pagination.pageSize}
                    onChange={(e) => table.setPageSize(Number(e.target.value))}
                    className="page-size-select"
                  >
                    {[25, 50, 100, 200].map((pageSize) => (
                      <option key={pageSize} value={pageSize}>
                        Show {pageSize}
                      </option>
                    ))}
                  </select>

                  <button
                    onClick={() => table.setPageIndex(0)}
                    disabled={!table.getCanPreviousPage()}
                    className="pagination-button"
                  >
                    {'<<'}
                  </button>
                  <button
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                    className="pagination-button"
                  >
                    {'<'}
                  </button>
                  <span className="page-number">
                    Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
                  </span>
                  <button
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                    className="pagination-button"
                  >
                    {'>'}
                  </button>
                  <button
                    onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                    disabled={!table.getCanNextPage()}
                    className="pagination-button"
                  >
                    {'>>'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default EndLineHistory;
